import React from 'react';
import {Button, ButtonGroup, Card, CardBody, Col, Row, UncontrolledAlert} from 'reactstrap';
import SmartDialog from "../SmartDialog/SmartDialog";
import MDSpinner from 'react-md-spinner';
import PropTypes from 'prop-types';
import TTLProxy from "../../common/TTLAsyncProxy";
import {TractorRequestEndpoints, USSDEndpoints} from "../../common/ProxyEndpoints";
import {ReactWizard, WizardPhase} from "../ReactWizard";
import {Form, Formik} from "formik";
import * as Yup from 'yup';
import HTML from '../../common/HtmlHelpers';
import Utils from "../../common/Utils";
import {
    calculateRequestCost,
    checkTractorAvailability,
    getServiceProductsList,
    getServiceRegions,
    serviceHasProductSelection
} from "../../views/Request/RequestUtils";
import {css} from "glamor";
import {ic_assignment} from 'react-icons-kit/md/ic_assignment';
import {ic_arrow_back} from 'react-icons-kit/md/ic_arrow_back';
import Icon from 'react-icons-kit';
import FormInput from "../FormInput";
import AppVariables from '../../common/AppVariables';
import _ from 'lodash';
import {ExpanderView, SectionedList} from "../AuditExplorer/Elements";
import PaymentMethods from "./PaymentMethods";
import MobileNetworks from '../../views/Request/MobileNetworks';
import TwoLineItemRender from "../Elements/TwoLineItemRender";
import DatePicker from 'react-datepicker';
import moment from 'moment';
import {getDistricts, getLocationRecord, getWards, listProvinces} from "../../data/locations";
import {DefaultBindingProduct} from "../../views/Extras/Services/USSDServices/USSDServicesCommon";
import LoadingOverlayView from 'react-loading-overlay';
import {Desktop, Mobile} from "../../common/ResponsiveUtils";
import CurrencyLabel from "../CurrencyLabel/CurrencyLabel";
import ProfileAvatar from "../ProfileAvatar";
import {
    formatQuantity,
    formatServiceQuantity,
    getActiveQuantityInfo,
    getQuantityUnit,
    getQuantityValue, getServiceUnit
} from "../../common/QuantityHelper";
import {ErrorView} from "./ErrorView";
import {LoadingView} from "./LoadingView";

const datePickerStyle = css({
    minWidth: 400
});

const cardStyle = css({
    cursor: "pointer",
    height: 128,
    boxShadow: '0 1px 2px #000',
    '&:hover': {
        backgroundColor: '#e2e2e2'
    }
});

const backButtonStyle = {
    cursor: "pointer"
};

const RequestTypes = {
    Booking: 'booking',
    Hiring: 'hiring'
};


const resolveNetworkOperator = (phone, defaultOperator) => {
    return defaultOperator;
};


const HeadView = ({title, message, info, back}) => {

    return <div className="clearfix">

        <div style={{display: "inline"}} className="pull-left">
            {back && <span style={backButtonStyle}><Icon icon={ic_arrow_back} size={24} onClick={back}/></span>}
        </div>

        <div className={back ? "ml-5" : ""}>
            <h6 className="text-left">{title}</h6>
            <div className="clearfix">

                <div className="pull-left">
                    <p style={{color: "#828282"}}>{message}</p>
                </div>

                <div className="pull-right">
                    <b style={{fontSize: 'smaller'}}>{info}</b>
                </div>
            </div>
        </div>

    </div>;
};


const RequestCostDisplay = ({requestCost, isCalculatingCost}) => {

    return requestCost ? <React.Fragment>

        <Desktop>
            <span className="font-xl">{isCalculatingCost && <MDSpinner
                size={18}/>} Service Cost: <b>{AppVariables.CurrencyCode} {requestCost.cost.toLocaleString('en-us')}</b></span>
        </Desktop>

        <Mobile>
            <UncontrolledAlert color={"success"}><i className="fa fa-info-circle"/> <span
                className="font-lg">{isCalculatingCost && <MDSpinner size={18}/>} Cost: <b><CurrencyLabel
                value={requestCost.cost}/></b></span></UncontrolledAlert>
        </Mobile>

    </React.Fragment> : null
};

/**
 * A dialog for building tractor requests
 */
class TractorRequestBuilder extends React.Component {


    static propTypes = {
        onRequestCreated: PropTypes.func,
        fetchFarmers: PropTypes.func,
        onBuildRequest: PropTypes.func
    };

    constructor(props) {
        super(props);
        this.state = TractorRequestBuilder.getInitialState();
        this.proxy = TTLProxy.getInstance();
        this.onSubmitTractorRequest = this.onSubmitTractorRequest.bind(this);

        this.ServiceValidationSchema = Yup.object().shape({
            service: Yup.mixed().required().label("Service"),
            pricing_group: Yup.mixed().required().label("Fuel payment"),
            binding_id: Yup.mixed().optional().label("Crop/Product"),
            quantity: Yup.number().required(`Please enter desired ${getActiveQuantityInfo().plural.toLowerCase()}`).label(`Quantity`)
        });

    }

    static getInitialState(state = {}) {

        return {
            isBusy: false,
            services: [],
            serviceRegions: [],
            isCalculatingCost: false,
            calculateCostError: null,
            requestCost: null,
            serviceFarmer: state ? (state.farmer || (state.request ? state.request.farmer : null)) : null,
            error: null,
            isSubmitting: false,
            request_type: null,
            request: state ? state.request : null,
            ...state
        };

    }

    /**
     * Load state for request
     * @returns {Promise<void>}
     */
    async loadState() {

        try {

            this.setState({isBusy: true, error: null});

            const res = await this.proxy.processRequest({
                ...USSDEndpoints.getUSSDServices(),
                include: ["pricing"]
            });

            this.setState({
                services: res.getPayload().filter(x => x.enable)
            });

        } catch (e) {

            const msg = Utils.getResponseError(e);

            HTML.toastWarning(msg);

            this.setState({
                error: msg
            });

        } finally {
            this.setState({isBusy: false});
        }

    }

    show(options = {}) {

        this.setState(TractorRequestBuilder.getInitialState(options), () => {

            //  show and load state
            this._dlg.show();

            //
            return this.loadState();
        });

    }

    /**
     *  On submit tractor request
     **/
    async onSubmitTractorRequest(service, payload, token, initializeTransaction) {

        try {

            //  allow modification of request payload
            let _extra = {};
            if (this.props.onBuildRequest) {
                _extra = await this.props.onBuildRequest({service, payload, token, initializeTransaction});
            }

            const res = await this.proxy.processRequest(_.merge(TractorRequestEndpoints.createTractorRequest(service, payload, token, undefined, initializeTransaction), _extra));
            if (res.ok) {

                //
                if (this.props.onRequestCreated) {
                    this.props.onRequestCreated(res);
                }
            }

            return res;
        } catch (e) {
            HTML.toastError(Utils.getResponseError(e));
        }
    }

    render() {

        const {isBusy, isSubmitting, serviceFarmer, services, request_type, error, request: req} = this.state;
        const proxy = this.proxy;

        return <SmartDialog ref={r => this._dlg = r}
                            title={<React.Fragment>
                                <div className="clearfix">

                                    <div style={{display: "inline"}} className="pull-left">
                                        <Icon icon={ic_assignment} size={32}/>
                                    </div>

                                    <div className="ml-5">
                                        <h5>Request Tractor Service</h5>
                                        {request_type && <p className="font-sm m-0">{(() => {

                                            switch (request_type) {
                                                case RequestTypes.Hiring:
                                                    return 'HIRING';
                                                case RequestTypes.Booking:
                                                    return 'BOOKING';
                                            }

                                        })()}</p>}
                                    </div>

                                </div>
                            </React.Fragment>}
                            classNames={"fullscreen-modal-dialog"}
                            contentClassName={"fullscreen-modal-content-w100 fullscreen-modal-content-mh100"}
                            large={true}>

            <LoadingView isBusy={isBusy} message="Initializing process, please hold on...">

                <ErrorView error={error}>
                    <ReactWizard ref={r => this._wizard = r}
                                 initialValues={{
                                     request_type: req ? (req.is_booking ? RequestTypes.Booking : RequestTypes.Hiring) : undefined,
                                     locationMode: req ? ((req.district && req.province && req.ward) ? 'select' : 'custom') : 'select',
                                     deadlineDate: req ? (req.is_booking ? moment(req.deadline_time).toDate() : null) : null
                                 }}
                                 renderAlways={false}>
                        {({setValue, getValues, getValue, deleteValues, next, prev, back, wizard}) => {

                            const getOptions = (values) => {

                                let service = values.service ? services.find(x => x.id === values.service.value) : null;
                                let servicePricingGroups = service ? getServiceRegions(service) : [];
                                let pricingGroup = values.pricing_group ? servicePricingGroups.find(x => x.id === values.pricing_group.value) : null;
                                let groupBindings = service ? (serviceHasProductSelection(service, pricingGroup) ? getServiceProductsList(service, pricingGroup) : []) : [];

                                return {
                                    service,
                                    servicePricingGroups,
                                    pricingGroup,
                                    groupBindings,
                                    selectedBinding: values.binding_id ? groupBindings.find(x => x.id === values.binding_id.value) : groupBindings.find(x => x.product === DefaultBindingProduct)
                                };


                            };

                            const getDefaultBinding = (values) => {
                                const service = services.find(x => x.id === values.service.value);
                                const pricingGroup = getServiceRegions(service).find(x => x.id === values.pricing_group.value);
                                const groupBindings = getServiceProductsList(service, pricingGroup);
                                return groupBindings.find(x => x.product === DefaultBindingProduct);
                            };

                            const updateRequestCost = async (values) => {

                                const {service, selectedBinding, pricingGroup} = getOptions(values);

                                let requestCost;

                                if(Number(values.quantity) > 0 && service && pricingGroup){

                                    try {
                                        setValue('isCalculatingCost', true);
                                        requestCost = await calculateRequestCost(values.quantity, service, pricingGroup, selectedBinding || getDefaultBinding(values));
                                        setValue( 'requestCost' , requestCost);
                                    }
                                    catch (e) {
                                        //  Failed
                                        requestCost = undefined;
                                    } finally {
                                        setValue('isCalculatingCost', false);
                                    }
                                } else {
                                    requestCost = null;
                                    setValue('requestCost', null);
                                }


                                return requestCost;
                            };

                            const finalizeRequest = async () => {

                                //  set finalizing flag
                                setValue('isFinalizingRequest', true);
                                setValue('finalizeError', null);

                                //  finalize on request cost
                                let cost = await updateRequestCost(getValue('serviceData'));
                                if (cost === undefined) {
                                    HTML.toastWarning('Failed finalizing service. Please try again');
                                    setValue('finalizeError', 'Failed finalizing request. Please try again');
                                }

                                //  clear flag
                                setValue('isFinalizingRequest', false);

                            };

                            const optionLabel = (text, value, instance) => ({label: text, text, value, instance});

                            const getDefaultPricingGroup = (service) => {
                                const regions = getServiceRegions(service);
                                const value = regions.find(x => x.active) || regions[0];
                                return value ? optionLabel(value.name, value.id, value) : null;
                            };

                            const getServiceInfoInitialValues = () => {

                                if (req) {

                                    return  {
                                        service: services && services.find(x => x.id === req.service.id) ? optionLabel(req.service.name,req.service.id,req.service) : null,
                                        pricing_group: req.price_binding ? optionLabel(req.price_binding.pricing_group.name,req.price_binding.pricing_group.id,req.price_binding.pricing_group) : null,
                                        binding_id: req.price_binding ? optionLabel(req.price_binding.product,req.price_binding.id,req.price_binding) : null,
                                        quantity: getQuantityValue(req,{ acre: 'acres' , hectare: 'hectares' })
                                    };

                                }

                                return {
                                    service: services ? ({
                                        label: services[0].name,
                                        text: services[0].name,
                                        value: services[0].id
                                    }) : null,
                                    pricing_group: AppVariables.IncludeSegmentedRequestCost ? null : getDefaultPricingGroup(services[0]),
                                    binding_id: null,
                                    quantity: ''
                                };

                            };

                            const getLocationInitialValues = () => {

                                if (req) {

                                    return {
                                        district: req.district ? optionLabel(req.district, req.district) : null,
                                        province: req.province ? optionLabel(req.province, req.province) : null,
                                        ward: req.ward ? optionLabel(req.ward, req.ward) : null,
                                        location: (_.isNil(req.ward) && _.isNil(req.province) && _.isNil(req.district)) ? {
                                            text: req.location,
                                            lat: req.location_lat,
                                            lng: req.location_long
                                        } : null
                                    }

                                }

                                return {
                                    district: null,
                                    province: null,
                                    ward: null,
                                    location: null
                                };

                            };

                            const getFarmerInitialValues = () => {

                                if (req) {

                                    return {
                                        farmer: this.state.serviceFarmer ? optionLabel(req.name, req.farmer_id, req.farmer || {}) : null,
                                        name: req.name,
                                        phone: req.phone,
                                        operator: resolveNetworkOperator(req.phone, MobileNetworks[0].name)
                                    };

                                }

                                return {
                                    farmer: this.state.serviceFarmer ? optionLabel(
                                        this.state.serviceFarmer.name,
                                        this.state.serviceFarmer.id,
                                        this.state.serviceFarmer) : null,
                                    name: '',
                                    phone: '',
                                    operator: MobileNetworks[0].name
                                };

                            };

                            const getPaymentInitialValues = () => {

                                const user_role = proxy.getUserRole();
                                const paymentMethods = PaymentMethods.filter(x => x.roles.includes(user_role));
                                let initialValues;
                                let selectedMethod;

                                if (req) {

                                    selectedMethod = paymentMethods.find(x => x.id.toLowerCase() === req.txn_operator.toLowerCase());
                                    if (selectedMethod) {

                                        initialValues = getValue('request_type') === 'booking' ?
                                            (selectedMethod.behaviours.booking.initialValues || selectedMethod.initialValues) :
                                            selectedMethod.initialValues;

                                        return {
                                            method: {label: selectedMethod.name, value: selectedMethod.id},
                                            ..._.pick(req, Object.keys(selectedMethod.validationSchema)),   // validation schema tell about required values
                                            ...(_.isFunction(initialValues) ? initialValues(getValues()) : initialValues)
                                        };

                                    }

                                }

                                selectedMethod = paymentMethods[0];
                                initialValues = getValue('request_type') === 'booking' ?
                                    (selectedMethod.behaviours.booking.initialValues || selectedMethod.initialValues) :
                                    selectedMethod.initialValues;

                                return {
                                    method: {label: selectedMethod.name, value: selectedMethod.id},
                                    ...(_.isFunction(initialValues) ? initialValues(getValues()) : initialValues)
                                };

                            };

                            return <React.Fragment>

                                {_.isNil(req) && <WizardPhase name={"select_request_type"}>

                                    <div className="text-center p-lg-5">

                                        <h5 className="mb-4">Hi, what request would you like to make today (:-</h5>

                                        <Row className="ml-4 mr-4">

                                            <Col md={6}>

                                                <Card className={`p-4 ${cardStyle}`} onClick={() => {

                                                    this.setState({
                                                        request_type: RequestTypes.Hiring
                                                    }, () => {
                                                        setValue('request_type', RequestTypes.Hiring);
                                                        setValue('deadlineDate', null);
                                                        next();
                                                    });

                                                }}>
                                                    <CardBody>
                                                        <h6>Hiring</h6>
                                                        <p>Pay to access tractor service immediately</p>
                                                    </CardBody>
                                                </Card>

                                            </Col>

                                            <Col md={6}>

                                                <Card className={`p-4 ${cardStyle}`} onClick={() => {

                                                    this.setState({
                                                        request_type: RequestTypes.Booking
                                                    }, () => {
                                                        setValue('request_type', RequestTypes.Booking);

                                                        next();
                                                    });

                                                }}>
                                                    <CardBody>
                                                        <h6>Booking</h6>
                                                        <p>Book or schedule a tractor service and pay later</p>
                                                    </CardBody>
                                                </Card>

                                            </Col>

                                        </Row>

                                    </div>

                                </WizardPhase>}

                                <WizardPhase name={"select_service"} onActivate={() => window.setTimeout(async () => {

                                    if (req && this._serviceForm) {
                                        await updateRequestCost(this._serviceForm.getFormikContext().values);
                                    }

                                }, 250)}>
                                    {() => {

                                        return <React.Fragment>

                                            <HeadView title={"Service Info"}
                                                      message="Select service and price option"
                                                      back={req ? null : back}
                                                      info="*"/>

                                            <hr className="m-0"/>

                                            <div className="p-4">

                                                <Formik onSubmit={async (values, {setSubmitting}) => {

                                                    if (values.service && values.pricing_group && !values.binding_id) {

                                                        let defaultBinding = getDefaultBinding(values);
                                                        if (defaultBinding) {
                                                            values.binding_id = {
                                                                text: defaultBinding.product,
                                                                value: defaultBinding.id
                                                            };
                                                        }

                                                    }

                                                    setValue('serviceData', {
                                                        ...values,
                                                        currency: AppVariables.CurrencyCode
                                                    });

                                                    next();

                                                    setSubmitting(false);
                                                }}
                                                        ref={r => this._serviceForm = r}
                                                        enableReinitialize
                                                        isInitialValid={!!req || !!getValue('serviceData')}
                                                        validationSchema={this.ServiceValidationSchema}
                                                        validate={async values => {

                                                            let errors = {};
                                                            if (!getValue('requestCost')) {

                                                                if (await updateRequestCost(values) === undefined) {
                                                                    errors.cost = "Failed calculating cost";
                                                                    HTML.toastWarning('Failed calculating service cost');
                                                                }

                                                            }

                                                            if (Object.keys(errors).length > 0)
                                                                throw errors;

                                                        }}
                                                        initialValues={_.assign(getServiceInfoInitialValues(), getValue('serviceData') || {})}>

                                                    {({isSubmitting, values, isValid, setFieldValue}) => {

                                                        const {
                                                            service,
                                                            servicePricingGroups,
                                                            groupBindings
                                                        } = getOptions(values);

                                                        const requestCost = getValue('requestCost');

                                                        return <Form>

                                                            <FormInput name="service"
                                                                       type="dropdown"
                                                                       items={services.map(x => ({
                                                                           text: x.name,
                                                                           value: x.id,
                                                                           instance: x
                                                                       }))}
                                                                       autoFocus={false}
                                                                       onChange={props => {
                                                                           setFieldValue('pricing_group', AppVariables.IncludeSegmentedRequestCost ? null : getDefaultPricingGroup(props.instance));
                                                                           setFieldValue('binding_id', null);
                                                                           setValue('requestCost', null);
                                                                       }}
                                                                       title="Service Type"
                                                                       placeholder="Select tractor service..."/>

                                                            {service && <React.Fragment>

                                                                {AppVariables.IncludeSegmentedRequestCost &&
                                                                    <FormInput name="pricing_group"
                                                                               type="dropdown"
                                                                               items={servicePricingGroups.map(x => ({
                                                                                   text: `${x.name} ${x.description ? ` (${x.description})` : ''}`.trim(),
                                                                                   value: x.id
                                                                               }))}
                                                                               title="Fuel Payment"
                                                                               required
                                                                               autoFocus={false}
                                                                               onChange={({value}) => {
                                                                                   setFieldValue('binding_id', null);
                                                                                   setTimeout(() => updateRequestCost(this._serviceForm.getFormikContext().values), 450);
                                                                               }}
                                                                               placeholder="Choose fuel supply payment option"/>}


                                                                {service && groupBindings.length > 0 && <React.Fragment>

                                                                    <FormInput name="binding_id"
                                                                               type="dropdown"
                                                                               items={groupBindings.filter(x => x.product !== DefaultBindingProduct).map(x => ({
                                                                                   text: x.product,
                                                                                   value: x.id
                                                                               })).concat([
                                                                                   {
                                                                                       text: "Other (Specify)",
                                                                                       value: ""
                                                                                   }
                                                                               ])}
                                                                               title="Crop/Product"
                                                                               autoFocus={false}
                                                                               onChange={({value}) => {
                                                                                   setTimeout(() => updateRequestCost(this._serviceForm.getFormikContext().values), 450);
                                                                               }}
                                                                               placeholder="Select crop/product from list"/>

                                                                    {values.binding_id && values.binding_id.value === "" &&
                                                                        <FormInput name={"custom_product_name"}
                                                                                   title={"Custom Crop/Product"}
                                                                                   placeholder={"Enter crop/product name..."}
                                                                                   type={"text"}/>}


                                                                </React.Fragment>}

                                                            </React.Fragment>}

                                                            <FormInput name="quantity"
                                                                       title={`Total ${getActiveQuantityInfo().singular}(s)`}
                                                                       type="number"
                                                                       min={0.4}
                                                                       step="0.1"
                                                                       max={999999999}
                                                                       onChange={(evt) => {
                                                                           setFieldValue("quantity",evt.target.value);
                                                                           setTimeout(() => updateRequestCost(this._serviceForm.getFormikContext().values), 450);
                                                                       }}
                                                                       placeholder={`Enter ${getActiveQuantityInfo().plural.toLowerCase()} required...`}/>


                                                            <hr/>

                                                            <Desktop>
                                                                <div className="clearfix">

                                                                    <div className="pull-left">
                                                                        <RequestCostDisplay requestCost={requestCost}
                                                                                            isCalculatingCost={getValue('isCalculatingCost')}/>
                                                                    </div>

                                                                    <div className="pull-right">
                                                                        <Button color="green"
                                                                                disabled={!isValid}>Next <i
                                                                            className="icon-arrow-right"/></Button>
                                                                    </div>

                                                                </div>
                                                            </Desktop>

                                                            <Mobile>
                                                                <RequestCostDisplay requestCost={requestCost}
                                                                                    isCalculatingCost={getValue('isCalculatingCost')}/>
                                                                <Button color="green" block disabled={!isValid}>Next <i
                                                                    className="icon-arrow-right"/></Button>
                                                            </Mobile>


                                                        </Form>
                                                    }}
                                                </Formik>

                                            </div>
                                        </React.Fragment>
                                    }}

                                </WizardPhase>

                                <WizardPhase name="selectLocation">

                                    {context => {

                                        const requestCost = getValue('requestCost');
                                        const locationOption = getValue('locationMode', 'select');

                                        const checkTractorsAvailability = async (values, showProgress) => {

                                            let result;

                                            if (values.province) {

                                                try {

                                                    if (showProgress)
                                                        setValue('checkingTractorAvailability', true);

                                                    const {serviceData} = getValues();

                                                    result = await checkTractorAvailability(
                                                        serviceData.service.value,
                                                        values.province ? values.province.value : null,
                                                        values.district ? values.district.value : null,
                                                        values.ward ? values.ward.value : null
                                                    );

                                                    setValue('tractorAvailability', result);
                                                } catch (e) {
                                                    result = undefined;
                                                } finally {

                                                    if (showProgress)
                                                        setValue('checkingTractorAvailability', false);

                                                }

                                            } else {
                                                result = null;
                                            }


                                            return result;
                                        };

                                        return <div className="p-lg-5">

                                            <Row>

                                                <Col md={1}>
                                                    {wizard.canMoveBack() &&
                                                        <Icon icon={ic_arrow_back} size={24} onClick={back}
                                                              style={backButtonStyle}/>}
                                                </Col>

                                                <Col md={10}>
                                                    <h5 className="mb-4 text-center">Enter or select select
                                                        location</h5>
                                                </Col>

                                            </Row>

                                            <Formik onSubmit={async (values, {setSubmitting}) => {

                                                const ensureUserConfirmation = result => {
                                                    const {total} = result;
                                                    return !(total === 0 && !window.confirm("Sorry, there's no service coverage in selected location. Do you still want to proceed?"));
                                                };


                                                const tractorsAvailability = getValue('tractorAvailability');
                                                if (!tractorsAvailability) {

                                                    const result = await checkTractorsAvailability(values, true);
                                                    if (result) {

                                                        if (!ensureUserConfirmation(result)) {
                                                            setSubmitting(false);
                                                            return;
                                                        }

                                                    } else if (result === undefined) {

                                                        HTML.toastWarning('Failed checking for availability of service within selected location. Please try again');
                                                        setSubmitting(false);
                                                        return;
                                                    }

                                                } else {

                                                    if (!ensureUserConfirmation(tractorsAvailability)) {
                                                        setSubmitting(false);
                                                        return;
                                                    }

                                                }

                                                switch (getValue('locationMode') || 'select') {
                                                    case "select":

                                                        const _locPayload = {
                                                            district: values.district.value,
                                                            province: values.province.value,
                                                            ward: values.ward.value
                                                        };

                                                        const loc = getLocationRecord(_locPayload);
                                                        setValue('locationInfo', {
                                                            location: [values.province.value, values.district.value, values.ward.value].join(","),
                                                            location_lat: loc ? loc.lat : null,
                                                            location_long: loc ? loc.lng : null,
                                                            ..._locPayload
                                                        });

                                                        break;
                                                    case "custom":

                                                        setValue('locationInfo', {
                                                            location: values.location.text,
                                                            location_lat: values.location.lat,
                                                            location_long: values.location.lng
                                                        });

                                                        break;
                                                }

                                                //  set data for navigation purposes
                                                setValue('locationInfoValues', values);

                                                setSubmitting(false);
                                                next();
                                            }}
                                                    ref={r => this._locationForm = r}
                                                    enableReinitialize
                                                    isInitialValid={!!req || !!getValue('locationInfoValues')}
                                                    validationSchema={locationOption === 'select' ? Yup.object().shape({
                                                        province: Yup.mixed().required().label(process.env.REACT_APP_LOCATION_COMP1),
                                                        district: Yup.mixed().required().label(process.env.REACT_APP_LOCATION_COMP2),
                                                        ward: Yup.mixed().required().label(process.env.REACT_APP_LOCATION_COMP3),
                                                    }) : Yup.object().shape({
                                                        location: Yup.mixed().required().label("Location")
                                                    })}
                                                    initialValues={_.assign(getLocationInitialValues(), getValue('locationInfoValues') || {})}>
                                                {({isValid, setFieldValue, values}) => {


                                                    const setLocationOption = value => {

                                                        this._locationForm.setValues({
                                                            district: '',
                                                            province: '',
                                                            ward: '',
                                                            location: null
                                                        });

                                                        setValue('locationMode', value);
                                                    };

                                                    const tractorAvailability = getValue('tractorAvailability');
                                                    const isFetchingTractorAvailability = getValue('checkingTractorAvailability', false);

                                                    return <Form>

                                                        {tractorAvailability && tractorAvailability.total === 0 &&
                                                            <UncontrolledAlert color={"warning"}><i
                                                                className="fa fa-exclamation-triangle"/> There are no
                                                                tractors service providers available in selected
                                                                location. You can however proceed. T's and C's
                                                                apply</UncontrolledAlert>}

                                                        <div className="text-center">
                                                            <ButtonGroup>
                                                                <Button active={locationOption === 'select'}
                                                                        onClick={() => setLocationOption('select')}><i
                                                                    className="fa fa-map"/> Select Known Place</Button>
                                                                <Button active={locationOption === 'custom'}
                                                                        onClick={() => setLocationOption('custom')}><i
                                                                    className="fa fa-map-marker"/> Enter
                                                                    Location</Button>
                                                            </ButtonGroup>
                                                        </div>


                                                        <div className="mt-2">
                                                            {locationOption === 'select' && <React.Fragment>

                                                                <FormInput name="province"
                                                                           type="dropdown"
                                                                           placeholder={`Select ${process.env.REACT_APP_LOCATION_COMP1.toLowerCase()}...`}
                                                                           items={listProvinces()}
                                                                           onChange={async (value) => {

                                                                               setFieldValue('district', null);
                                                                               setFieldValue('ward', null);

                                                                               await checkTractorsAvailability({
                                                                                   ...values,
                                                                                   province: value
                                                                               });

                                                                           }}
                                                                           title={process.env.REACT_APP_LOCATION_COMP1}
                                                                           required
                                                                           autoFocus={false}/>

                                                                <FormInput name="district"
                                                                           type="dropdown"
                                                                           placeholder={`Select ${process.env.REACT_APP_LOCATION_COMP2.toLowerCase()}...`}
                                                                           items={values.province ? getDistricts(values.province.value) : []}
                                                                           onChange={async (value) => {

                                                                               setFieldValue('ward', null);

                                                                               await checkTractorsAvailability({
                                                                                   ...values,
                                                                                   district: value
                                                                               });

                                                                           }}
                                                                           title={process.env.REACT_APP_LOCATION_COMP2}
                                                                           required
                                                                           autoFocus={false}/>


                                                                <FormInput name="ward"
                                                                           type="dropdown"
                                                                           placeholder={`Select ${process.env.REACT_APP_LOCATION_COMP3.toLowerCase()}...`}
                                                                           items={values.province && values.district ? getWards(values.province.value, values.district.value) : []}
                                                                           onChange={async (value) => {

                                                                               await checkTractorsAvailability({
                                                                                   ...values,
                                                                                   ward: value
                                                                               });

                                                                           }}
                                                                           title={process.env.REACT_APP_LOCATION_COMP3}
                                                                           required
                                                                           autoFocus={false}/>

                                                            </React.Fragment>}

                                                            {locationOption === 'custom' && <React.Fragment>

                                                                <FormInput name="location"
                                                                           title="Location"
                                                                           type="location"
                                                                           icon="fa fa-map-marker"
                                                                           placeholder="Enter custom location here..."/>

                                                            </React.Fragment>}
                                                        </div>

                                                        <hr/>

                                                        <Desktop>
                                                            <div className="clearfix">

                                                                <div className="pull-left">
                                                                    <RequestCostDisplay requestCost={requestCost}
                                                                                        isCalculatingCost={getValue('isCalculatingCost')}/>
                                                                </div>

                                                                <div className="pull-right">
                                                                    <Button color="green" disabled={!isValid}>
                                                                        {isFetchingTractorAvailability ?
                                                                            <span><MDSpinner size={16}/> Checking service availability...</span> :
                                                                            <span>Next <i className="icon-arrow-right"/></span>}
                                                                    </Button>
                                                                </div>

                                                            </div>
                                                        </Desktop>

                                                        <Mobile>

                                                            <RequestCostDisplay requestCost={requestCost}
                                                                                isCalculatingCost={getValue('isCalculatingCost')}/>

                                                            <Button block color="green" disabled={!isValid}>
                                                                {isFetchingTractorAvailability ?
                                                                    <span><MDSpinner size={16}/> Checking service availability...</span> :
                                                                    <span>Next <i className="icon-arrow-right"/></span>}
                                                            </Button>

                                                        </Mobile>


                                                    </Form>

                                                }}
                                            </Formik>


                                        </div>
                                    }}

                                </WizardPhase>

                                <WizardPhase name={"farmerInfo"}>
                                    {(context) => {

                                        const requestCost = getValue('requestCost');

                                        return <React.Fragment>

                                            <HeadView title={"Farmer Info"}
                                                      message="Enter/Select the farmer the service is required for"
                                                      back={back}
                                                      info="*"/>

                                            <hr className="m-0"/>

                                            <div className="p-4">

                                                <Formik onSubmit={(values, {setSubmitting}) => {

                                                    setValue('farmerData', values);
                                                    setSubmitting(false);

                                                    if (getValue('request_type') === RequestTypes.Booking)
                                                        next();
                                                    else
                                                        wizard.offsetPhase(2);

                                                }}
                                                        ref={r => this._farmerInfoForm = r}
                                                        validationSchema={this.props.fetchFarmers ? Yup.object().shape({
                                                            farmer: Yup.mixed().required().label("Farmer"),
                                                            operator: Yup.string().required().label("Network Operator")
                                                        }) : Yup.object().shape({
                                                            name: Yup.string()
                                                                .required()
                                                                .label("Farmer name"),

                                                            phone: Yup.string()
                                                                .required()
                                                                .test(value => Utils.validatePhoneNumber(value, this._farmerInfoForm.getFormikContext().values['phone-region']))
                                                                .label("Phone number"),

                                                            operator: Yup.string().required().label("Network Operator")
                                                        })}
                                                        isInitialValid={!!req || !!getValue('farmerData')}
                                                        initialValues={_.assign(getFarmerInitialValues(), getValue('farmerData') || {})}>

                                                    {({}) => {

                                                        return <Form>

                                                            {this.props.fetchFarmers ? <FormInput name="farmer"
                                                                                                  title="Farmer"
                                                                                                  type="dropdown"
                                                                                                  autoFocus={false}
                                                                                                  async
                                                                                                  defaultOptions
                                                                                                  dropdownItemRender={(props) => {

                                                                                                      const {
                                                                                                          name,
                                                                                                          phone,
                                                                                                          media
                                                                                                      } = props.data.instance;
                                                                                                      return <TwoLineItemRender
                                                                                                          line1Value={name}
                                                                                                          line2Prefix={"Phone"}
                                                                                                          line2Value={phone}
                                                                                                          media={media}/>;
                                                                                                  }}
                                                                                                  loadOptions={async (value) => {

                                                                                                      const response = await this.props.fetchFarmers({
                                                                                                          fields: ['id', 'name', 'phone'],
                                                                                                          include: ['media'],
                                                                                                          search: {
                                                                                                              keyword: value,
                                                                                                              method: "inside",
                                                                                                              fields: ["name", "phone", "email"]
                                                                                                          },
                                                                                                          pagination: {
                                                                                                              limit: 5,
                                                                                                              offset: 0
                                                                                                          }
                                                                                                      });

                                                                                                      return (Array.isArray(response) ? response : response.getPayload()).map(v => ({
                                                                                                          label: v.name,
                                                                                                          value: v.id,
                                                                                                          instance: v
                                                                                                      }));
                                                                                                  }}
                                                                                                  icon="fa fa-user"
                                                                                                  placeholder="Select farmer"
                                                                                                  required/> :
                                                                <React.Fragment>

                                                                    <FormInput name="name"
                                                                               title={"Farmer Name"}
                                                                               type="text"
                                                                               placeholder="Enter the name of the farmer..."
                                                                               icon="fa fa-user"
                                                                               required/>

                                                                    <FormInput name="phone"
                                                                               type="phone"
                                                                               title={"Phone Number"}
                                                                               placeholder="Enter the farmer's phone number"
                                                                               icon="fa fa-phone"
                                                                               required/>


                                                                </React.Fragment>}


                                                            <FormInput name="operator"
                                                                       title={"Network Operator (Phone number)"}
                                                                       comment={<span className="text-muted mb-1">Select the mobile network operator for phone number</span>}
                                                                       type="select"
                                                                       items={MobileNetworks.map(x => ({
                                                                           text: x.name,
                                                                           value: x.name
                                                                       }))}
                                                                       icon="fa fa-signal"
                                                                       required/>

                                                            <hr/>

                                                            <Desktop>
                                                                <div className="clearfix">

                                                                    <div className="pull-left">
                                                                        <RequestCostDisplay requestCost={requestCost}
                                                                                            isCalculatingCost={getValue('isCalculatingCost')}/>
                                                                    </div>

                                                                    <div className="pull-right">
                                                                        <Button color="green">Proceed <i
                                                                            className="icon-arrow-right"/></Button>
                                                                    </div>

                                                                </div>
                                                            </Desktop>

                                                            <Mobile>
                                                                <RequestCostDisplay requestCost={requestCost}
                                                                                    isCalculatingCost={getValue('isCalculatingCost')}/>
                                                                <Button block color="green">Proceed <i
                                                                    className="icon-arrow-right"/></Button>
                                                            </Mobile>


                                                        </Form>

                                                    }}
                                                </Formik>


                                            </div>

                                        </React.Fragment>
                                    }}

                                </WizardPhase>

                                <WizardPhase name="selectDate">
                                    {(context) => {

                                        return <div className="text-center p-lg-5">

                                            <Row className="text-left">

                                                <Col md={1}>
                                                    {wizard.canMoveBack() &&
                                                        <Icon icon={ic_arrow_back} size={24} onClick={back}
                                                              style={backButtonStyle}/>}
                                                </Col>

                                                <Col md={10}>
                                                    <h3 className="text-center mb-4">When is service required?</h3>
                                                </Col>

                                            </Row>

                                            <p>Select the date service is required</p>
                                            <DatePicker dropdownMode={"scroll"}
                                                        placeholderText={"Click to select date..."}
                                                        minDate={moment().add(5, 'days').toDate()}
                                                        maxDate={moment().add(6, 'month').toDate()}
                                                        className={`form-control form-input-select p-3 ${datePickerStyle} text-center`}
                                                        selected={getValue('deadlineDate')}
                                                        onSelect={value => {
                                                            setValue('deadlineDate', value);
                                                        }}/>


                                            <hr/>

                                            <div className="text-center">
                                                <Button color="green" onClick={() => {
                                                    wizard.offsetPhase(2);
                                                }} disabled={!getValue('deadlineDate')}>Proceed <i
                                                    className="icon-arrow-right"/></Button>
                                            </div>

                                        </div>


                                    }}
                                </WizardPhase>

                                <WizardPhase name={"payment"} onActivate={() => {

                                    //  remove payment info
                                    deleteValues(['paymentInitialValues', 'paymentInfo']);

                                    if (!getValue('paymentInitialValues')) {
                                        setValue('paymentInitialValues', getPaymentInitialValues());
                                    }

                                }}>
                                    {() => {

                                        const user_role = this.proxy.getUserRole();
                                        const paymentMethods = PaymentMethods.filter(x => x.roles.includes(user_role));

                                        return <React.Fragment>

                                            <HeadView title={"Service Payment"}
                                                      message="Select payment method for service"
                                                      back={back}
                                                      info="*"/>

                                            <hr className="m-0"/>

                                            <div className="p-4">

                                                <Formik onSubmit={(values, {setSubmitting}) => {

                                                    setValue('paymentInfo', values);
                                                    wizard.offsetPhase(2);
                                                    setSubmitting(false);

                                                }}
                                                        validate={values => {

                                                            const paymentInfo = paymentMethods.find(x => x.id === values.method.value);
                                                            if (paymentInfo && paymentInfo.customValidation) {
                                                                return paymentInfo.customValidation(values, getValues());
                                                            }

                                                            return {};
                                                        }}
                                                        enableReinitialize
                                                        isInitialValid={!!req || !!getValue('paymentInfo')}
                                                        initialValues={getValue('paymentInitialValues') || {
                                                            method: {
                                                                label: paymentMethods[0].name,
                                                                value: paymentMethods[0].id
                                                            }
                                                        }}>

                                                    {(form) => {

                                                        const {values, isValid} = form;
                                                        const requestCost = getValue('requestCost');
                                                        const paymentInfo = paymentMethods.find(x => x.id === values.method.value);
                                                        let PaymentComponent = paymentInfo.component;

                                                        return <Form>

                                                            <FormInput name="method"
                                                                       type="dropdown"
                                                                       items={paymentMethods.map(p => ({
                                                                           text: p.name,
                                                                           value: p.id
                                                                       }))}
                                                                       autoFocus={false}
                                                                       title="Payment Method"
                                                                       onChange={(selected) => {

                                                                           const {value} = selected;
                                                                           const user_role = this.proxy.getUserRole();
                                                                           const instance = PaymentMethods.find(x => x.roles.includes(user_role) && x.id == value);
                                                                           const _initialValues = _.isFunction(instance.initialValues) ? instance.initialValues(getValues()) : instance.initialValues;

                                                                           setValue('paymentInitialValues', {
                                                                               ..._.pick(values, Object.keys(_initialValues)),
                                                                               method: selected,
                                                                               ..._initialValues
                                                                           });

                                                                       }}
                                                                       placeholder="Select payment method..."/>

                                                            <hr/>

                                                            <PaymentComponent logo={paymentInfo.logo}
                                                                              form={form}/>

                                                            <hr/>

                                                            <Desktop>

                                                                <div className="clearfix">

                                                                    <div className="pull-left">
                                                                        <RequestCostDisplay requestCost={requestCost}
                                                                                            isCalculatingCost={getValue('isCalculatingCost')}/>
                                                                    </div>

                                                                    <div className="pull-right">
                                                                        <Button color="green">Confirm Request <i
                                                                            className="icon-arrow-right"/></Button>
                                                                    </div>

                                                                </div>

                                                            </Desktop>

                                                            <Mobile>
                                                                <RequestCostDisplay requestCost={requestCost}
                                                                                    isCalculatingCost={getValue('isCalculatingCost')}/>
                                                                <Button block color="green">Confirm Request <i
                                                                    className="icon-arrow-right"/></Button>
                                                            </Mobile>


                                                        </Form>
                                                    }}


                                                </Formik>


                                            </div>

                                        </React.Fragment>
                                    }}
                                </WizardPhase>


                                <WizardPhase name={"payment-booking"} onActivate={() => {

                                    //  remove payment info
                                    deleteValues(['paymentInitialValues', 'paymentInfo']);

                                    if (!getValue('paymentInitialValues')) {
                                        setValue('paymentInitialValues', getPaymentInitialValues());
                                    }

                                }}>
                                    {() => {

                                        const user_role = this.proxy.getUserRole();
                                        const paymentMethods = PaymentMethods.filter(x => x.roles.includes(user_role) && x.behaviours.booking.enabled);

                                        return <React.Fragment>

                                            <HeadView title={"Service Payment"}
                                                      message="Select payment method for service"
                                                      back={back}
                                                      info="*"/>

                                            <hr className="m-0"/>

                                            <Formik onSubmit={(values, {setSubmitting}) => {
                                                setValue('paymentInfo', values);
                                                next();
                                                setSubmitting(false);
                                            }}
                                                    validate={values => {

                                                        const paymentInfo = paymentMethods.find(x => x.id === values.method.value);
                                                        if (paymentInfo && paymentInfo.behaviours.booking.renderComponent && paymentInfo.customValidation) {
                                                            return paymentInfo.customValidation(values, getValues());
                                                        }

                                                        return {};
                                                    }}
                                                    enableReinitialize
                                                    isInitialValid={!!req || !!getValue('paymentInfo')}
                                                    initialValues={getValue('paymentInitialValues') || {
                                                        method: {
                                                            label: paymentMethods[0].name,
                                                            value: paymentMethods[0].id
                                                        }
                                                    }}>

                                                {(form) => {

                                                    const {values, isValid} = form;
                                                    const requestCost = getValue('requestCost');
                                                    const paymentInfo = paymentMethods.find(x => x.id === values.method.value);
                                                    let PaymentComponent = paymentInfo.component;
                                                    let PlaceHolderComponent = paymentInfo.behaviours.booking.placeholderComponent;

                                                    return <Form>

                                                        <FormInput name="method"
                                                                   type="dropdown"
                                                                   items={paymentMethods.map(p => ({
                                                                       text: p.name,
                                                                       value: p.id
                                                                   }))}
                                                                   autoFocus={false}
                                                                   title="Payment Method"
                                                                   onChange={(selected) => {

                                                                       console.log('Changes ', selected);

                                                                       const {value} = selected;
                                                                       const user_role = this.proxy.getUserRole();
                                                                       const instance = PaymentMethods.find(x => x.roles.includes(user_role) && x.id == value);
                                                                       const initialValues = instance.behaviours.booking.initialValues || instance.initialValues;
                                                                       const _initialValues = _.isFunction(initialValues) ? initialValues(getValues()) : initialValues;

                                                                       setValue('paymentInitialValues', {
                                                                           ..._.pick(values, Object.keys(_initialValues)),
                                                                           method: selected,
                                                                           ..._initialValues
                                                                       });

                                                                   }}
                                                                   placeholder="Select payment method..."/>

                                                        <hr/>

                                                        {paymentInfo.behaviours.booking.renderComponent &&
                                                            <React.Fragment>
                                                                <PaymentComponent logo={paymentInfo.logo}
                                                                                  form={form}/>
                                                                <hr/>
                                                            </React.Fragment>}

                                                        {PlaceHolderComponent && <PlaceHolderComponent/>}


                                                        <Desktop>

                                                            <div className="clearfix">

                                                                <div className="pull-left">
                                                                    <RequestCostDisplay requestCost={requestCost}
                                                                                        isCalculatingCost={getValue('isCalculatingCost')}/>
                                                                </div>

                                                                <div className="pull-right">
                                                                    <Button color="green">Confirm Request <i
                                                                        className="icon-arrow-right"/></Button>
                                                                </div>

                                                            </div>

                                                        </Desktop>

                                                        <Mobile>
                                                            <RequestCostDisplay requestCost={requestCost}
                                                                                isCalculatingCost={getValue('isCalculatingCost')}/>
                                                            <Button block color="green">Confirm Request <i
                                                                className="icon-arrow-right"/></Button>
                                                        </Mobile>


                                                    </Form>
                                                }}
                                            </Formik>
                                        </React.Fragment>
                                    }}
                                </WizardPhase>

                                <WizardPhase name={"confirm"} onActivate={() => finalizeRequest()}>
                                    {() => {

                                        const {
                                            paymentInfo,
                                            farmerData,
                                            serviceData,
                                            locationInfo,
                                            requestCost,
                                            deadlineDate
                                        } = getValues();

                                        let serviceDetails = [];
                                        let farmerInfo = [];
                                        let paymentDetails = [];
                                        let locationDetails = [];
                                        let farmer;

                                        //  service details
                                        if (serviceData) {

                                            if (serviceData.service) {
                                                serviceDetails.push({
                                                    title: "Service",
                                                    value: serviceData.service.text
                                                });
                                            }

                                            if(serviceData.quantity){

                                                const serviceInstance = services.find(x => x.id === serviceData.service.value);

                                                serviceDetails.push({
                                                    title: "Quantity",
                                                    value: `${serviceData.quantity} ${getServiceUnit(serviceData.quantity, { service: serviceInstance }).toLowerCase()}`
                                                });
                                            }

                                            if (serviceData.pricing_group) {
                                                serviceDetails.push({
                                                    title: "Fuel Supply",
                                                    value: serviceData.pricing_group.text
                                                });
                                            }

                                            if (serviceData.binding_id && !serviceData.custom_product_name && serviceData.binding_id.text !== DefaultBindingProduct) {
                                                serviceDetails.push({
                                                    title: "Product/Crop",
                                                    value: serviceData.binding_id.text
                                                });
                                            }

                                            if (serviceData.custom_product_name && !serviceData.binding_id) {
                                                serviceDetails.push({
                                                    title: "Product/Crop",
                                                    value: serviceData.custom_product_name
                                                });
                                            }

                                            if (requestCost) {

                                                serviceDetails.push({
                                                    title: "Service Cost",
                                                    value: `${AppVariables.CurrencyCode} ${requestCost.cost.toLocaleString('en-us')}`
                                                });

                                            }


                                        }

                                        if (locationInfo) {

                                            if (locationInfo.district && locationInfo.province && locationInfo.ward) {
                                                locationDetails = locationDetails.concat([
                                                    {
                                                        title: process.env.REACT_APP_LOCATION_COMP1,
                                                        value: locationInfo.province,
                                                    },
                                                    {
                                                        title: process.env.REACT_APP_LOCATION_COMP2,
                                                        value: locationInfo.district
                                                    },
                                                    {
                                                        title: process.env.REACT_APP_LOCATION_COMP3,
                                                        value: locationInfo.ward
                                                    }
                                                ]);
                                            } else {

                                                locationDetails.push({
                                                    title: "Place",
                                                    value:
                                                        <span>{locationInfo.location} {locationInfo.location_lat !== null && locationInfo.location_lng !== null &&
                                                            <i className="fa fa-map-marker"/>}</span>
                                                });

                                            }
                                        }

                                        //  farmer info
                                        if (farmerData) {


                                            if (farmerData.farmer)
                                                farmer = {
                                                    ...farmerData,
                                                    name: farmerData.farmer.instance.name,
                                                    phone: farmerData.farmer.instance.phone,
                                                };
                                            else
                                                farmer = farmerData;


                                            if (farmer)
                                                farmerInfo = farmerInfo.concat([
                                                    {
                                                        title: "Name",
                                                        value: farmer.name
                                                    },
                                                    {
                                                        title: "Phone No.",
                                                        value: farmer.phone
                                                    }
                                                ]);
                                        }

                                        if (paymentInfo) {

                                            //  payment
                                            const selectPaymentMethod = paymentInfo.method.value;
                                            const paymentMethod = PaymentMethods.find(x => x.id === selectPaymentMethod);

                                            paymentDetails.push({
                                                title: "Payment Method",
                                                value: paymentMethod.name
                                            });

                                            paymentDetails = paymentDetails.concat(paymentMethod.getPaymentDetails(paymentInfo));
                                        }

                                        if (deadlineDate) {

                                            serviceDetails.push({
                                                title: "Booking Date",
                                                value: moment(deadlineDate).format('L')
                                            });

                                        }

                                        return <React.Fragment>

                                            <LoadingOverlayView active={getValue('isSubmittingRequest')} spinner
                                                                text={"Submitting request..."}>

                                                <LoadingView isBusy={getValue('isFinalizingRequest')}
                                                             message="Just a moment. Finalizing request...">

                                                    <ErrorView error={getValue('finalizeError')}
                                                               retry={() => finalizeRequest()}>

                                                        <HeadView title={"Confirm Tractor Request"}
                                                                  message={`Please confirm ${getValue('request_type')} details?`}
                                                                  back={back}/>

                                                        <div className="p-4">

                                                            <ExpanderView className="mb-2" title={"Service Details"}
                                                                          isExpanded>
                                                                <SectionedList items={serviceDetails}/>
                                                            </ExpanderView>

                                                            <ExpanderView className="mb-2" title={"Location Info"}
                                                                          isExpanded>
                                                                <SectionedList items={locationDetails}/>
                                                            </ExpanderView>

                                                            <ExpanderView className="mb-2" title={"Farmer Info"}
                                                                          isExpanded>
                                                                <SectionedList items={farmerInfo}/>
                                                            </ExpanderView>

                                                            {paymentDetails.length > 0 &&
                                                                <ExpanderView className="mb-2" title={"Payment Info"}
                                                                              isExpanded>
                                                                    <SectionedList items={paymentDetails}/>
                                                                </ExpanderView>}

                                                            <hr/>

                                                            <Button block color="green" onClick={async () => {

                                                                if (getValue('isSubmittingRequest'))
                                                                    return;


                                                                setValue('isSubmittingRequest', true);

                                                                try {

                                                                    /*
                                                                        name: state.name || state['farmerInfo'].name,
                                                                        phone: utils.toStandardPhone(ctx.Mobile),
                                                                        location: state.farm_location,
                                                                        operator: ctx.Operator,
                                                                        cost: state.totalCost,
                                                                        pricing_group: region ? region.id : null,
                                                                        pricing_id: binding ? binding.id : null,
                                                                        quantity: Number(state["quantity"]),
                                                                        txn_phone: state.txn_phone ? utils.toStandardPhone(state.txn_phone === "1" ? ctx.Mobile : state.txn_phone) : null,
                                                                        txn_operator: paymentMethod ? paymentMethod.operator : null,
                                                                        txn_currency: config.currency,
                                                                        district: state.district,
                                                                        province: state.province,
                                                                        ward: state.ward,
                                                                        location_lat: state.location_lat,
                                                                        location_long: state.location_long,
                                                                        apply_discount: false,
                                                                        deadline_time: state.deadline_time,

                                                                        //  If provider doesn't mask user request coordinates
                                                                        source_lat: state.source_lat,
                                                                        source_long: state.source_long,
                                                                        metadata
                                                                     */


                                                                    let payload = {
                                                                        name: farmer.name,
                                                                        phone: farmer.phone,
                                                                        farmer_id: farmerData.farmer ? farmerData.farmer.id : undefined,
                                                                        operator: farmer.operator,

                                                                        //  append location info
                                                                        ...locationInfo,

                                                                            cost: requestCost.cost,
                                                                            quantity: serviceData.quantity,
                                                                            pricing_group: serviceData.pricing_group ? serviceData.pricing_group.value: null,
                                                                            pricing_id: serviceData.binding_id ? serviceData.binding_id.value : null,
                                                                            txn_currency: serviceData.currency,

                                                                        apply_discount: false,
                                                                        request_source: "WEB",
                                                                        metadata: {
                                                                            'custom_product_name': serviceData.custom_product_name
                                                                        }
                                                                    };

                                                                    if (deadlineDate) {
                                                                        payload.deadline_time = moment(deadlineDate).toDate().toJSON();
                                                                    }

                                                                    let initializePayment;
                                                                    let paymentMethod;
                                                                    if (paymentInfo) {

                                                                        const {method, ...paymentValues} = paymentInfo;
                                                                        paymentMethod = PaymentMethods.find(x => x.id === method.value);
                                                                        initializePayment = paymentMethod.behaviours.afterSubmit.initializePayment;

                                                                        payload = {
                                                                            ...payload,
                                                                            ...paymentMethod.prepareValues ? paymentMethod.prepareValues(paymentValues) : paymentValues
                                                                        };

                                                                    }

                                                                    const response = await this.onSubmitTractorRequest(serviceData.service.value, payload, undefined, initializePayment);
                                                                    if (response && response.ok) {

                                                                        if (paymentMethod && paymentMethod.behaviours.afterSubmit.customCheckout) {
                                                                            setValue('submitResponseData', response.getPayload());
                                                                            wizard.offsetPhase(1);
                                                                        } else {
                                                                            this._dlg.hide();
                                                                            HTML.toastSuccess('Tractor request submitted successfully');
                                                                        }
                                                                    }

                                                                } catch (e) {
                                                                    //  Failed submitting request
                                                                } finally {
                                                                    setValue('isSubmittingRequest', false);
                                                                }

                                                            }}>Submit Tractor Request <i
                                                                className="fa fa-check"/></Button>

                                                        </div>
                                                    </ErrorView>

                                                </LoadingView>

                                            </LoadingOverlayView>

                                        </React.Fragment>
                                    }}

                                </WizardPhase>

                                <WizardPhase name={"customCheckout"} component={({context}) => {

                                    const {
                                        paymentInfo,
                                        farmerData,
                                        submitResponseData
                                    } = getValues();

                                    const {method, ...paymentValues} = paymentInfo;
                                    const paymentMethod = PaymentMethods.find(x => x.id === method.value);
                                    const PaymentCheckoutComponent = paymentMethod.checkoutInfo.requestBuilderComponent;
                                    return <PaymentCheckoutComponent request={submitResponseData}
                                                                     farmer={farmerData.farmer ? {
                                                                         name: farmerData.farmer.instance.name,
                                                                         phone: farmerData.farmer.instance.phone,
                                                                         email: farmerData.farmer.instance.email
                                                                     } : farmerData}
                                                                     paymentData={paymentValues}
                                                                     closeDialog={() => this._dlg.hide()}/>;

                                }}/>
                            </React.Fragment>
                        }}
                    </ReactWizard>
                </ErrorView>

            </LoadingView>

        </SmartDialog>
    }

}

export default TractorRequestBuilder;



// WEBPACK FOOTER //
// ./src/components/TractorRequestBuilder/TractorRequestBuilder.js